import { useCallback, useEffect, useRef, useState } from "react";
import { graphsApi } from "../api/graphs";

export interface GraphRunEvent {
  type: "node_start" | "node_end" | "stream" | "error" | "interrupt" | "complete" | "done" | "artifact";
  nodeId?: string;
  data: Record<string, unknown>;
  timestamp: string;
}

export interface GraphRunState {
  status: "idle" | "running" | "succeeded" | "failed" | "interrupted";
  events: GraphRunEvent[];
  runId: string | null;
  error: string | null;
}

const POLL_MS = 2000;
// Backstop so a run that never reaches a terminal/parked state can't poll
// forever (~10 min at 2s). HITL parks are detected explicitly below.
const MAX_POLLS = 300;

/**
 * Hook for queue-driven graph execution. The orchestrator runs each node as a
 * durable work item (no SSE/streaming), so this starts a run via the
 * company-scoped endpoint and POLLS the run's aggregate status until it reaches
 * a terminal state (succeeded/failed). Events are derived from the run's items.
 */
export function useGraphRun(companyId?: string | null) {
  const [state, setState] = useState<GraphRunState>({
    status: "idle",
    events: [],
    runId: null,
    error: null,
  });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stoppedRef = useRef(false);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => clearPoll(), [clearPoll]);

  const run = useCallback(
    async (graphId: string, input?: { state?: Record<string, unknown>; messages?: Array<{ role: "user" | "assistant"; content: string }> }) => {
      if (!companyId) {
        setState({ status: "failed", events: [], runId: null, error: "No practice selected" });
        return;
      }
      clearPoll();
      stoppedRef.current = false;
      setState({ status: "running", events: [], runId: null, error: null });

      let runId: string;
      try {
        const res = await graphsApi.run(companyId, graphId, input);
        runId = res.runId;
      } catch (err) {
        setState({ status: "failed", events: [], runId: null, error: err instanceof Error ? err.message : String(err) });
        return;
      }
      setState((s) => ({ ...s, runId }));

      let polls = 0;
      const poll = async () => {
        if (stoppedRef.current) return;
        try {
          const runData = await graphsApi.getRun(companyId, graphId, runId);
          const events = (runData.events ?? []) as unknown as GraphRunEvent[];
          if (runData.status === "succeeded" || runData.status === "failed") {
            clearPoll();
            setState({
              status: runData.status === "succeeded" ? "succeeded" : "failed",
              events,
              runId,
              error: runData.status === "failed" ? "One or more steps failed" : null,
            });
            return;
          }
          // A run parked at an interrupt / human_review node is non-terminal but
          // shouldn't poll forever — surface "awaiting review" and stop.
          const items = ((runData as { items?: Array<{ status?: string }> }).items ?? []);
          if (items.some((it) => it.status === "needs_approval")) {
            clearPoll();
            setState({ status: "interrupted", events, runId, error: null });
            return;
          }
          setState((s) => ({ ...s, events, runId }));
          if (++polls >= MAX_POLLS) {
            clearPoll();
            setState((s) => ({ ...s, status: "failed", error: "Run timed out (still in progress on the server)" }));
          }
        } catch {
          // Transient (run may not have any items committed yet); keep polling.
        }
      };

      void poll();
      pollRef.current = setInterval(() => void poll(), POLL_MS);
    },
    [companyId, clearPoll],
  );

  const stop = useCallback(() => {
    stoppedRef.current = true;
    clearPoll();
    setState((s) => (s.status === "running" ? { ...s, status: "failed", error: "Cancelled" } : s));
  }, [clearPoll]);

  return { ...state, run, stop };
}
